import { Stack, TextField, TextFieldProps } from '@mui/material';
import React, { useMemo, useState, useEffect } from 'react';
import { debounce } from 'src/utils/debounce';

export type DebouncedTextFieldProps = TextFieldProps & {
  value: string;
  keyValue: string;
  onValueChange: (value: string) => void; // 入力時に即時更新するコールバック
  onDebouncedChange: (value: string) => void; // debounce後に呼び出すコールバック
  debounceTime?: number;
  orderId?: number;
}

function DebouncedTextField({
  value,
  keyValue,
  onValueChange,
  onDebouncedChange,
  debounceTime = 500,
  orderId = 0,
  ...otherProps
}: DebouncedTextFieldProps) {
  const [isComposing, setIsComposing] = useState(false);

  const debouncedChange = useMemo(
    () => debounce(
        (val: string) => {
            onDebouncedChange(val);
        },
        debounceTime
    ),
    [orderId] // 依存配列を空にする
  );

  useEffect(() => () => {
    debouncedChange.cancel();
  }, [orderId]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onValueChange(e.target.value);
    if (!isComposing) {
      debouncedChange(e.target.value);
    }
  };

  const handleCompositionStart = () => {
    setIsComposing(true);
  };

  const handleCompositionEnd = (e: React.CompositionEvent<HTMLInputElement>) => {
    setIsComposing(false);
    debouncedChange((e.target as HTMLInputElement).value);
  };

  return (
    <Stack spacing={0} alignItems="flex-start">
      <TextField
        fullWidth
        value={value}
        onChange={handleChange}
        onCompositionStart={handleCompositionStart}
        onCompositionEnd={handleCompositionEnd}
        {...otherProps}
      />
    </Stack>
  );
}

export default DebouncedTextField;
